import { useMemo } from "react";
import { useSelector as useReduxSelector } from "react-redux";
import { usePriceDropPredictionDetails } from "@apac/hooks";
import { DisruptionOffersSelectors } from "@b2bportal/core-disruption";
import { useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  DisruptionOfferQuoteSelectionEnum,
  type IFintechLineItem,
  type IPriceLineData,
  type IPriceLineItem,
  type ISummaryLineData,
  type ISummaryLineItem,
  SummaryLineType,
  TripCategory,
} from "@b2bportal/core-types";
import { Product } from "@b2bportal/purchase-api";
import {
  FlightSelectors,
  useCheckoutStateSelector as useSelector,
} from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import { PriceBreakdown } from "@hsbc/flights";
import { twoFractionOptions } from "@hsbc/utilities";
import {
  getCurrencyDifferenceFromQuotebreakdown,
  getCurrencyFromPricingInfo,
  getFintechLineItemsFromQuoteBreakdown,
  getIsPriceBreakdownLoading,
  getSellAtCurrency,
  getSummaryLineItems,
  getTripPriceLineItems,
} from "../../selectors";
import styles from "./styles.module.scss";

export interface ConnectedPriceBreakdownProps {
  showFareDescription?: boolean;
}

export const ConnectedPriceBreakdown = ({
  showFareDescription = true,
}: ConnectedPriceBreakdownProps) => {
  const COMPONENT_KEY = CoreFlightsComponent.FlightPriceBreakdown;
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t, formatFiatCurrency } = useI18nContext();
  const tripCurrency = useSelector(getCurrencyFromPricingInfo);
  const isPriceBreakdownLoading = useSelector(getIsPriceBreakdownLoading);
  const tripPriceLine: IPriceLineData[] = useSelector(getTripPriceLineItems);
  const fintechLineItemsFromQuoteBreakdown = useSelector(
    getFintechLineItemsFromQuoteBreakdown
  );
  const tripPricingSummary: ISummaryLineData[] =
    useSelector(getSummaryLineItems);
  const isCurrencyDifference = useSelector(
    getCurrencyDifferenceFromQuotebreakdown
  );
  const selectedDisruptionOffer = useReduxSelector(
    DisruptionOffersSelectors.getSelectedDisruptionOffer
  );
  const tripCategory = useSelector(FlightSelectors.getTripCategoryParent);

  const sellAtCurrency = useSelector(getSellAtCurrency);

  const { isPDPEligible, priceDropAmount } = usePriceDropPredictionDetails();

  const tripPriceLineItems: IPriceLineItem[] = useMemo(() => {
    return tripPriceLine.map((p) => {
      const personPriceBreakdown = {
        lineTitle: p.person
          ? `${p.person.givenName} ${p.person.surname}${
              p?.total?.fiat
                ? ` [${formatFiatCurrency(p?.total?.fiat, twoFractionOptions)}]`
                : ""
            }`
          : undefined,
        baseAmount: {
          label: t("core-checkout.priceBreakdown.baseFare"),
          value: formatFiatCurrency(p.baseAmount?.fiat, twoFractionOptions),
        },
        taxesAndFees: {
          label: t("core-checkout.priceBreakdown.taxesFees"),
          value: formatFiatCurrency(p.taxesAndFees?.fiat, twoFractionOptions),
        },
        products:
          p.products?.map((product) => {
            return {
              label: t(product.type),
              value: formatFiatCurrency(product.price.fiat, twoFractionOptions),
              type: product.type,
            };
          }) ?? [],
      };

      return personPriceBreakdown;
    });
  }, [formatFiatCurrency, t, tripPriceLine]);

  const summaryLineItems: ISummaryLineItem[] = useMemo(() => {
    return tripPricingSummary.map((p) => {
      switch (p.type) {
        case SummaryLineType.TOTAL: {
          return {
            label: t("core-checkout.priceBreakdown.total"),
            value: (
              <div>{formatFiatCurrency(p.price.fiat, twoFractionOptions)}</div>
            ),
            type: p.type,
          };
        }
      }
    });
  }, [t, tripPricingSummary]);

  const fintechLineItems: IFintechLineItem[] = useMemo(() => {
    const lineItems: IFintechLineItem[] = [];

    if (
      selectedDisruptionOffer?.selection ===
      DisruptionOfferQuoteSelectionEnum.Selected
    ) {
      lineItems.push({
        title: t("core-disruption.productName"),
        price: formatFiatCurrency(
          selectedDisruptionOffer.quote.premiumAmountLocalized?.fiat,
          twoFractionOptions
        ),
      });
    }

    function productLineItemToTitle(productLineItemType: Product) {
      switch (productLineItemType) {
        case Product.AirCfar:
          return t("core-flights-cfar.checkout.cfar");
        default:
          console.warn("Unknown product line item type", productLineItemType);
          return "";
      }
    }

    lineItems.push(
      ...fintechLineItemsFromQuoteBreakdown.map((productLineItem) => ({
        title: productLineItemToTitle(productLineItem.type),
        price: formatFiatCurrency(
          productLineItem.totalPremiumAmount,
          twoFractionOptions
        ),
      }))
    );

    return lineItems;
  }, [
    selectedDisruptionOffer,
    fintechLineItemsFromQuoteBreakdown,
    t,
    formatFiatCurrency,
  ]);

  return (
    <PriceBreakdown
      className={block}
      isLoading={isPriceBreakdownLoading}
      priceLineItems={tripPriceLineItems}
      summaryLineItems={summaryLineItems}
      fintechLineItems={fintechLineItems}
      currencyString={
        isCurrencyDifference
          ? t("chargedInCurrency", {
              currency:
                sellAtCurrency?.currencyCode || tripCurrency?.currencyCode,
            })
          : null
      }
      priceDropAmount={isPDPEligible ? priceDropAmount?.amount : undefined}
      title={t("priceBreakdown")}
      subtitle={
        showFareDescription && (
          <p>
            {tripCategory === TripCategory.ROUND_TRIP
              ? t("flightPriceBreakdown.roundTripDescription")
              : t("flightPriceBreakdown.oneWayDescription")}
          </p>
        )
      }
    ></PriceBreakdown>
  );
};
